import React, { useEffect, useRef, useState } from "react";
import { FiPlus } from "react-icons/fi";
import { showSuccessToast } from "../../constants/ToastService";
import { qualityApi } from "./qualityApi";
import { CASE_TYPES, Modal, hasPerm } from "./qualityUi";
import useQualityMeta, { subModulesOf } from "./useQualityMeta";

/**
 * Add or edit a test case in the global library.
 *
 * One form for every entry point: the library itself, a sprint ticket's Test
 * Cases tab (`context.ticketCode`), and a production bug's preventive action
 * (`context.bugId` + `context.actionId`). The last one arrives pre-filled from
 * the bug so the "add the missing test" step is quick to do properly.
 *
 * The fields are the ones a tester needs to run a case without guessing:
 * preconditions, and an expected result per step rather than one at the end.
 */

const blankStep = () => ({ action: "", expected: "" });

const fromCase = (tc) => ({
    title: tc.title || "",
    module: tc.module || "",
    sub_module: tc.sub_module || "",
    case_type: tc.case_type || "Positive",
    priority: tc.priority || "Medium",
    regression: tc.critical ? "critical" : tc.in_regression === false ? "none" : "regression",
    status: tc.status || "Active",
    preconditions: tc.preconditions || "",
    test_data: tc.test_data || "",
    steps: (tc.steps && tc.steps.length ? tc.steps : [blankStep()]).map((s) => ({ ...s })),
    applies_to: tc.applies_to || "all",
    setting_name: tc.setting_name || "",
    owner: tc.owner || "",
    tags: tc.tags || "",
});

// Same rules the backend enforces; checked here too so the user sees every
// problem at once instead of one round trip per mistake.
const validate = (f) => {
    const errors = [];
    const steps = f.steps.filter((s) => s.action.trim() || s.expected.trim());
    if (!f.title.trim()) errors.push("Add a title.");
    if (!f.sub_module) errors.push("Choose the screen / sub-module.");
    if (!f.preconditions.trim()) errors.push("Add the preconditions (the setup needed before step 1).");
    if (!steps.length) errors.push("Add at least one step.");
    steps.forEach((s, i) => {
        if (!s.action.trim()) errors.push(`Step ${i + 1} has an expected result but no action.`);
        if (!s.expected.trim()) errors.push(`Step ${i + 1} needs an expected result.`);
    });
    if (f.applies_to === "setting" && !f.setting_name.trim()) errors.push("Name the setting this case depends on.");
    return { errors, steps };
};

export default function TestCaseForm({ testCase, prefill, context = {}, onClose, onSaved, onDeleted, onOpenOther }) {
    const meta = useQualityMeta();
    const editing = !!(testCase && testCase.id);
    const [f, setF] = useState(() => fromCase({ ...(prefill || {}), ...(testCase || {}) }));
    const [errors, setErrors] = useState([]);
    const [similar, setSimilar] = useState([]);
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [inUse, setInUse] = useState("");
    const canDelete = editing && !!onDeleted && hasPerm("Manage Test Cases");
    const lastStepRef = useRef(null);
    const focusNewStep = useRef(false);

    const subs = subModulesOf(meta, f.module);
    const set = (patch) => setF((prev) => ({ ...prev, ...patch }));

    // Fill defaults once reference data arrives.
    useEffect(() => {
        if (!meta.modules.length) return;
        setF((prev) => {
            const module = prev.module || meta.modules[0].name;
            const list = subModulesOf(meta, module);
            return {
                ...prev,
                module,
                sub_module: list.includes(prev.sub_module) ? prev.sub_module : list[0] || "",
                owner: prev.owner || meta.members[0] || "",
            };
        });
    }, [meta]);

    // Duplicate check while typing the title, debounced.
    useEffect(() => {
        const t = setTimeout(() => {
            if (f.title.trim().length < 8) { setSimilar([]); return; }
            qualityApi.similarTestCases(f.title, testCase ? testCase.code : "")
                .then((res) => setSimilar(res.data || []))
                .catch(() => setSimilar([]));
        }, 300);
        return () => clearTimeout(t);
    }, [f.title, testCase]);

    useEffect(() => {
        if (focusNewStep.current && lastStepRef.current) {
            lastStepRef.current.focus();
            focusNewStep.current = false;
        }
    }, [f.steps.length]);

    const setStep = (i, key, value) =>
        setF((prev) => ({ ...prev, steps: prev.steps.map((s, k) => (k === i ? { ...s, [key]: value } : s)) }));
    const addStep = () => { focusNewStep.current = true; setF((prev) => ({ ...prev, steps: [...prev.steps, blankStep()] })); };
    const removeStep = (i) => setF((prev) => ({ ...prev, steps: prev.steps.filter((_, k) => k !== i) }));

    const save = async (e) => {
        e.preventDefault();
        const { errors: errs, steps } = validate(f);
        if (errs.length) { setErrors(errs); return; }
        setSaving(true);
        const payload = {
            title: f.title.trim(), module: f.module, sub_module: f.sub_module,
            case_type: f.case_type, priority: f.priority,
            in_regression: f.regression !== "none", critical: f.regression === "critical",
            status: f.status, preconditions: f.preconditions.trim(), test_data: f.test_data.trim(),
            steps: steps.map((s) => ({ action: s.action.trim(), expected: s.expected.trim() })),
            applies_to: f.applies_to, setting_name: f.applies_to === "setting" ? f.setting_name.trim() : "",
            owner: f.owner, tags: f.tags.trim(),
        };
        if (!editing) {
            if (context.ticketId) payload.ticket_id = context.ticketId;
            else if (context.ticketCode) payload.ticket_code = context.ticketCode;
            if (context.bugId) { payload.bug_id = context.bugId; payload.action_id = context.actionId; }
        }
        try {
            const res = editing
                ? await qualityApi.updateTestCase(testCase.id, payload)
                : await qualityApi.createTestCase(payload);
            showSuccessToast(res.message || "Test case saved.");
            onSaved && onSaved(res.data);
        } catch (err) {
            setErrors(err.payload?.errors?.length ? err.payload.errors : [err.message || "Could not save the test case."]);
        } finally {
            setSaving(false);
        }
    };

    const doDelete = async () => {
        setSaving(true);
        try {
            const res = await qualityApi.deleteTestCase(testCase.id);
            showSuccessToast(res.message || `${testCase.code} deleted.`);
            onDeleted(res.data);
        } catch (err) {
            setConfirmDelete(false);
            // A case that is release history can't be deleted; offer to retire it.
            if (err.payload?.code === "in_use") setInUse(err.message);
            else setErrors([err.message || "Could not delete the test case."]);
        } finally {
            setSaving(false);
        }
    };

    const deprecate = async () => {
        setSaving(true);
        try {
            const res = await qualityApi.updateTestCase(testCase.id, { status: "Deprecated" });
            showSuccessToast(`${testCase.code} deprecated. It stays in history and is left out of future runs.`);
            onSaved && onSaved(res.data);
        } catch (err) {
            setErrors(err.payload?.errors?.length ? err.payload.errors : [err.message || "Could not deprecate the test case."]);
        } finally {
            setSaving(false);
        }
    };

    const linked = [
        ...((testCase && testCase.linked_tickets) || []),
        ...(context.ticketCode && !editing ? [context.ticketCode] : []),
    ];
    const linkedBugs = [
        ...((testCase && testCase.linked_bugs) || []),
        ...(context.bugCode && !editing ? [context.bugCode] : []),
    ];

    return (
        <Modal
            wide
            title={editing ? <>Edit <span className="qa-mono">{testCase.code}</span></> : "New test case"}
            onClose={onClose}
            footer={confirmDelete ? (
                <>
                    <span className="qa-hint" style={{ color: "var(--qa-red)" }}>
                        Delete {testCase.code}? It is removed from the library and from any tickets it is linked to.
                    </span>
                    <span className="qa-sp" />
                    <button type="button" className="qa-btn" onClick={() => setConfirmDelete(false)} disabled={saving}>Keep it</button>
                    <button type="button" className="qa-btn qa-btn--danger" onClick={doDelete} disabled={saving}>
                        {saving ? "Deleting…" : `Delete ${testCase.code}`}
                    </button>
                </>
            ) : (
                <>
                    {canDelete && (
                        <button type="button" className="qa-btn qa-btn--danger" onClick={() => { setInUse(""); setConfirmDelete(true); }}>
                            Delete
                        </button>
                    )}
                    <span className="qa-hint">
                        {f.status === "Active"
                            ? `Active cases join the next regression run for ${f.module || "this module"}.`
                            : "Not Active, so it stays out of regression runs."}
                    </span>
                    <span className="qa-sp" />
                    <button type="button" className="qa-btn" onClick={onClose}>Cancel</button>
                    <button type="submit" form="qa-tc-form" className="qa-btn qa-btn--primary" disabled={saving}>
                        {saving ? "Saving…" : editing ? "Save changes" : "Add to library"}
                    </button>
                </>
            )}
        >
            <form id="qa-tc-form" onSubmit={save} noValidate>
                {inUse && (
                    <div className="qa-banner qa-banner--warn">
                        <span className="qa-banner__tx"><b>Can't delete.</b> {inUse}</span>
                        {f.status !== "Deprecated" && (
                            <button type="button" className="qa-btn qa-btn--sm" onClick={deprecate} disabled={saving}>
                                Mark as Deprecated
                            </button>
                        )}
                    </div>
                )}
                {errors.length > 0 && (
                    <div className="qa-banner qa-banner--bad">
                        <span className="qa-banner__tx">
                            <b>Fix these before saving:</b>
                            {errors.map((x) => <div key={x}>{x}</div>)}
                        </span>
                    </div>
                )}
                {context.bugCode && !editing && (
                    <div className="qa-banner qa-banner--info">
                        <span className="qa-banner__tx">
                            Preventive action for <b>{context.bugCode}</b>. The bug scenario is copied into the
                            preconditions and the type is set to {f.case_type}. Write steps that reproduce what slipped through.
                        </span>
                    </div>
                )}

                <div className="qa-fsec">
                    <div className="qa-sec"><h4>Basics</h4></div>
                    <div className="qa-form">
                        <label className="qa-fl qa-full">
                            <span className="qa-req">Title</span>
                            <input
                                id="qa-tc-title" className="qa-input" value={f.title}
                                onChange={(e) => set({ title: e.target.value })}
                                placeholder="What is being checked, e.g. Pending amount after a partial sales return"
                            />
                            {similar.length > 0 && (
                                <div className="qa-dups">
                                    <b>Similar cases already in the library.</b> Could you extend one of these instead of adding a near-copy?
                                    {similar.map((s) => (
                                        <button type="button" key={s.code} onClick={() => onOpenOther && onOpenOther(s)}>
                                            {s.code} · {s.title} ({s.sub_module})
                                        </button>
                                    ))}
                                </div>
                            )}
                        </label>
                        <label className="qa-fl">
                            <span className="qa-req">Module</span>
                            <select
                                id="qa-tc-module" className="qa-input" value={f.module}
                                onChange={(e) => set({ module: e.target.value, sub_module: subModulesOf(meta, e.target.value)[0] || "" })}
                            >
                                {meta.modules.map((m) => <option key={m.name} value={m.name}>{m.name}</option>)}
                            </select>
                        </label>
                        <label className="qa-fl">
                            <span className="qa-req">Screen / sub-module</span>
                            <select id="qa-tc-sub" className="qa-input" value={f.sub_module} onChange={(e) => set({ sub_module: e.target.value })}>
                                {subs.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </label>
                        <label className="qa-fl">
                            <span className="qa-req">Case type</span>
                            <select id="qa-tc-type" className="qa-input" value={f.case_type} onChange={(e) => set({ case_type: e.target.value })}>
                                {Object.keys(CASE_TYPES).map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                            <span className="qa-typehint">{CASE_TYPES[f.case_type]}</span>
                        </label>
                        <label className="qa-fl">
                            <span className="qa-req">Priority</span>
                            <select id="qa-tc-priority" className="qa-input" value={f.priority} onChange={(e) => set({ priority: e.target.value })}>
                                <option value="High">High · always run</option>
                                <option value="Medium">Medium</option>
                                <option value="Low">Low · run when time allows</option>
                            </select>
                        </label>
                    </div>
                </div>

                <div className="qa-fsec">
                    <div className="qa-sec"><h4>Preconditions &amp; test data</h4></div>
                    <div className="qa-form">
                        <label className="qa-fl qa-full">
                            <span className="qa-req">Preconditions</span>
                            <textarea
                                id="qa-tc-pre" className="qa-input" value={f.preconditions}
                                onChange={(e) => set({ preconditions: e.target.value })}
                                placeholder="Setup needed before step 1, e.g. Bill-cancel window = 24h, logged in as admin"
                            />
                        </label>
                        <label className="qa-fl qa-full">
                            Test data
                            <input
                                id="qa-tc-data" className="qa-input" value={f.test_data}
                                onChange={(e) => set({ test_data: e.target.value })}
                                placeholder="e.g. UHID 10045 · item PARA-500 · amount ₹1,250"
                            />
                        </label>
                    </div>
                </div>

                <div className="qa-fsec">
                    <div className="qa-sec"><h4>Steps <span className="qa-hint">One action per step, each with its own expected result</span></h4></div>
                    <table className="qa-steps">
                        <thead>
                            <tr><th /><th className="qa-req">Step</th><th className="qa-req">Expected result</th><th /></tr>
                        </thead>
                        <tbody>
                            {f.steps.map((s, i) => (
                                <tr key={i}>
                                    <td className="qa-steps__n">{i + 1}</td>
                                    <td>
                                        <textarea
                                            id={`qa-tc-step-${i}`} className="qa-input" value={s.action} placeholder="Action"
                                            aria-label={`Step ${i + 1}`}
                                            ref={i === f.steps.length - 1 ? lastStepRef : undefined}
                                            onChange={(e) => setStep(i, "action", e.target.value)}
                                        />
                                    </td>
                                    <td>
                                        <textarea
                                            id={`qa-tc-exp-${i}`} className="qa-input" value={s.expected} placeholder="What should happen"
                                            aria-label={`Expected result for step ${i + 1}`}
                                            onChange={(e) => setStep(i, "expected", e.target.value)}
                                        />
                                    </td>
                                    <td className="qa-steps__x">
                                        {f.steps.length > 1 && (
                                            <button type="button" className="qa-x" onClick={() => removeStep(i)} aria-label={`Remove step ${i + 1}`}>×</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <button type="button" className="qa-btn qa-btn--sm" style={{ margin: "6px 0 0 36px" }} onClick={addStep}>
                        <FiPlus size={11} /> Add step
                    </button>
                </div>

                <div className="qa-fsec">
                    <div className="qa-sec"><h4>Scope &amp; ownership</h4></div>
                    <div className="qa-form">
                        <label className="qa-fl">
                            Regression
                            <select id="qa-tc-reg" className="qa-input" value={f.regression} onChange={(e) => set({ regression: e.target.value })}>
                                <option value="regression">Include in regression</option>
                                <option value="critical">Regression + critical (every run)</option>
                                <option value="none">Not in regression</option>
                            </select>
                        </label>
                        <label className="qa-fl">
                            Status
                            <select id="qa-tc-status" className="qa-input" value={f.status} onChange={(e) => set({ status: e.target.value })}>
                                <option value="Active">Active · used in runs</option>
                                <option value="Draft">Draft · not used yet</option>
                                <option value="Deprecated">Deprecated · kept for history</option>
                            </select>
                        </label>
                        <label className="qa-fl">
                            Applies to
                            <select id="qa-tc-scope" className="qa-input" value={f.applies_to} onChange={(e) => set({ applies_to: e.target.value })}>
                                <option value="all">All hospitals</option>
                                <option value="setting">Only hospitals with a setting on</option>
                            </select>
                        </label>
                        {f.applies_to === "setting" ? (
                            <label className="qa-fl">
                                <span className="qa-req">Setting</span>
                                <input
                                    id="qa-tc-setting" className="qa-input" value={f.setting_name}
                                    onChange={(e) => set({ setting_name: e.target.value })} placeholder="e.g. Bill cancel window enabled"
                                />
                            </label>
                        ) : <div />}
                        <label className="qa-fl">
                            Owner
                            <select id="qa-tc-owner" className="qa-input" value={f.owner} onChange={(e) => set({ owner: e.target.value })}>
                                {meta.members.map((m) => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </label>
                        <label className="qa-fl">
                            Tags
                            <input
                                id="qa-tc-tags" className="qa-input" value={f.tags}
                                onChange={(e) => set({ tags: e.target.value })} placeholder="e.g. billing-critical, month-end"
                            />
                        </label>
                        <div className="qa-fl qa-full">
                            Linked
                            <div className="qa-meta">
                                {linked.map((t) => <span key={t} className="qa-chip qa-chip--accent">{t}</span>)}
                                {linkedBugs.map((b) => <span key={b} className="qa-chip qa-chip--red">{b}</span>)}
                                {!linked.length && !linkedBugs.length && (
                                    <span className="qa-faint">Not linked yet. Link it from a ticket's Test Cases tab.</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </Modal>
    );
}
